import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface KnowledgeBaseFile {
    _id: string;
    fileName: string;
    fileUrl: string;
    fileType: string;
    createdAt: string;
}

interface GetKnowledgeBaseFilesResponse {
    success: boolean;
    data: KnowledgeBaseFile[];
}

interface CommonResponse {
    success: boolean;
    message?: string;
}

interface UploadKnowledgeBaseFilesResponse extends CommonResponse {
    data?: any;
}

export const knowledgeBaseApi = createApi({
    reducerPath: 'knowledgeBaseApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.NEXT_PUBLIC_API_URL}/knowledge-base`,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as any).auth?.token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['KnowledgeBase'],
    endpoints: (builder) => ({
        getKnowledgeBaseFiles: builder.query<GetKnowledgeBaseFilesResponse, void>({
            query: () => '/',
            providesTags: ['KnowledgeBase'],
        }),
        uploadToKnowledgeBase: builder.mutation<UploadKnowledgeBaseFilesResponse, File[]>({
            query: (files) => {
                const formData = new FormData();
                files.forEach((file) => {
                    formData.append('files', file);
                });
                return {
                    url: '/',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['KnowledgeBase'],
        }),
        deleteKnowledgeBaseFile: builder.mutation<CommonResponse, string>({
            query: (id) => ({
                url: `/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['KnowledgeBase'],
        }),
        updateKnowledgeBaseFileName: builder.mutation<CommonResponse, { id: string; fileName: string }>({
            query: ({ id, fileName }) => ({
                url: `/${id}`,
                method: 'PATCH',
                body: { fileName },
            }),
            invalidatesTags: ['KnowledgeBase'],
        }),
    }),
});

export const {
    useGetKnowledgeBaseFilesQuery,
    useUploadToKnowledgeBaseMutation,
    useDeleteKnowledgeBaseFileMutation,
    useUpdateKnowledgeBaseFileNameMutation,
} = knowledgeBaseApi;
